import { HTMLAttributes } from 'react';

import Link from 'next/link';
import { arrayOf, bool, func, shape, string } from 'prop-types';

import { CategoryBlock } from '@/components/Atoms/CategoryBlock/CategoryBlock';
import { classMerge } from '@/utils/class-names';
import { removeTrailingSlash } from '@/utils/Slug';

export interface SecondNavProps extends HTMLAttributes<HTMLDivElement> {
	/**
	 * The category title to be added to the SecondNav.
	 */
	title: string;
	/**
	 * The state that indicates given category is vertical or not.
	 */
	isVertical?: boolean;
	/**
	 *  The tabs to be displayed.
	 */
	tabs?: SecondLevelItem[];
	// The category slug to be added to the SecondNav.
	categorySlug?: string;
	//  The article slug to be added to the SecondNav.
	articleSlug?: string;
}

/**
 * SecondNav Library Component
 */
export const SecondNav = ({
	className,
	title,
	isVertical,
	tabs,
	categorySlug,
	articleSlug,
	...props
}: SecondNavProps) => {
	const navStyles = classMerge(
		'flex flex-col md:flex-row gap-4',
		'border-neutral-grey-70 border-b',
		className
	);

	const tabStyles = classMerge(
		'font-display font-bold uppercase pb-3 whitespace-nowrap items-center',
		'text-body-5 md:text-body pb-2 text-neutral-grey-45',
		'border-y-2 border-transparent hover:text-vertical-primary'
	);

	return (
		<div data-cy="SecondNav" {...props} className={navStyles}>
			<CategoryBlock
				className="mb-2 text-display-3 md:min-w-[300px]"
				hasBlock={isVertical}
			>
				{title}
			</CategoryBlock>

			<div className="flex overflow-x-auto gap-x-5 items-center pb-1 -mb-1 remove-scrollbar">
				{tabs?.map((tab: SecondLevelItem) => (
					<Link
						key={tab.slug}
						href={
							tab.name === `All`
								? `/${removeTrailingSlash(categorySlug ?? '')}`
								: `/${removeTrailingSlash(categorySlug ?? '')}/${tab.slug}`
						}
					>
						<a
							className={classMerge(
								tabStyles,
								`${
									articleSlug === tab.slug ||
									(!articleSlug && tab.name === `All`)
										? `border-b-vertical-primary text-vertical-primary`
										: ``
								}`
							)}
						>
							{tab.name}
						</a>
					</Link>
				))}
			</div>
		</div>
	);
};

SecondNav.propTypes = {
	title: string,
	isVertical: bool,
	onTabChange: func,
	tabs: arrayOf(
		shape({
			name: string,
			slug: string,
		})
	),
};
